import React, { useState, useEffect } from 'react';
import { Search, CreditCard, CheckCircle, AlertCircle, RefreshCw, Layers } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { FilterCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';

const RechargeManagement = () => {
  const [history, setHistory] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processedByFilter, setProcessedByFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('history'); // 'history', 'queue'

  useEffect(() => {
    fetchRecharges();
  }, []);

  const fetchRecharges = async () => {
    setLoading(true);
    try {
      const histRes = await axios.get(`${constant.API_BASE_URL}/admin/business/recharges/history`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      const queueRes = await axios.get(`${constant.API_BASE_URL}/admin/business/recharges/wrong-id-queue`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (histRes.data.success) setHistory(histRes.data.data);
      if (queueRes.data.success) setQueue(queueRes.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveWrongId = async (id) => {
    if (!window.confirm("Approve wrong ID correction? Coins will be immediately credited to the user's wallet.")) return;
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/recharges/wrong-id-queue/${id}/approve`, {}, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert(res.data.message);
        fetchRecharges();
      }
    } catch (err) {
      alert("Failed to approve");
    }
  };

  const filteredHistory = history.filter(item => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = !s ||
      (item.email && item.email.toLowerCase().includes(s)) ||
      (item.first_name && item.first_name.toLowerCase().includes(s)) ||
      (item.last_name && item.last_name.toLowerCase().includes(s)) ||
      (item.country && item.country.toLowerCase().includes(s));
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesProcessedBy = processedByFilter === 'all' || 
      (processedByFilter === 'admin' && (item.processor_type === 'admin' || item.processor_type === 'seller' || item.processor_type === 'agency' || (!!item.creator_first && item.processor_type !== 'api' && !item.recharge_type?.toLowerCase().includes('self')))) || 
      (processedByFilter === 'api' && (item.processor_type === 'api' || !item.creator_first || item.recharge_type?.toLowerCase().includes('self') || item.recharge_type?.toLowerCase().includes('gateway') || item.recharge_type?.toLowerCase().includes('api')));
    const matchesCountry = countryFilter === 'ALL' || (item.country && item.country.toLowerCase() === countryFilter.toLowerCase());
    return matchesSearch && matchesStatus && matchesProcessedBy && matchesCountry;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Recharge Management</h2>
          <p className="text-slate-500 mt-1">Audit manual coin deposits, verify failed recharges, and approve corrected user-ID recharges.</p>
        </div>
        <button 
          onClick={fetchRecharges}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          Reload Recharge Logs
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-6">
        <button 
          onClick={() => setActiveTab('history')}
          className={`pb-4 text-sm font-bold border-b-2 transition ${
            activeTab === 'history' ? 'border-pink-600 text-pink-600' : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          All Recharge History ({history.length})
        </button>
        <button 
          onClick={() => setActiveTab('queue')}
          className={`pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
            activeTab === 'queue' ? 'border-pink-600 text-pink-600' : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          Wrong ID Approvals Queue ({queue.length})
          {queue.length > 0 && <span className="w-2 h-2 rounded-full bg-red-500 animate-ping"></span>}
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Search Toolbar */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search user recharges..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-48">
              <FilterCountrySelect
                value={countryFilter}
                onChange={setCountryFilter}
                placeholder="All Countries"
              />
            </div>
            {activeTab === 'history' && (
              <>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full sm:w-48 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-slate-600 font-medium"
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select
                  value={processedByFilter}
                  onChange={(e) => setProcessedByFilter(e.target.value)}
                  className="w-full sm:w-48 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-slate-600 font-medium"
                >
                  <option value="all">All Processors</option>
                  <option value="admin">Coin Seller / Admin</option>
                  <option value="api">Self / API Integration</option>
                </select>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Syncing transaction ledger...</p>
          </div>
        ) : activeTab === 'history' ? (
          filteredHistory.length === 0 ? (
            <div className="p-20 text-center text-slate-400">
              <CreditCard size={48} className="mx-auto mb-4 opacity-30" />
              <p>No recharges recorded.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-6">Recharge ID</th>
                    <th className="p-6">Target User</th>
                    <th className="p-6">Location</th>
                    <th className="p-6">Amount (USD)</th>
                    <th className="p-6">Coins Credited</th>
                    <th className="p-6">Processed By</th>
                    <th className="p-6">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredHistory.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-6 font-mono text-xs text-slate-400">{item.id.substring(0, 13)}...</td>
                      <td className="p-6 font-bold text-slate-800">
                        {item.first_name} {item.last_name}
                        <p className="text-xs font-normal text-slate-400 mt-0.5">{item.email}</p>
                      </td>
                      <td className="p-6">
                        <CountryBadge country={item.country} />
                      </td>
                      <td className="p-6 font-bold text-slate-700">${parseFloat(item.amount).toFixed(2)}</td>
                      <td className="p-6 font-black text-pink-600">{parseInt(item.coins).toLocaleString()}</td>
                      <td className="p-6 text-slate-500">
                        <div className="font-medium text-slate-700">
                          {item.creator_first ? `${item.creator_first} ${item.creator_last}` : 'Self / API Integration'}
                        </div>
                        {item.recharge_type && (
                          <span className="text-[11px] text-pink-600 bg-pink-50 px-2 py-0.5 rounded font-semibold inline-block mt-0.5">
                            {item.recharge_type}
                          </span>
                        )}
                      </td>
                      <td className="p-6">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                          item.status === 'approved' ? 'bg-green-100 text-green-700' :
                          item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          queue.length === 0 ? (
            <div className="p-20 text-center text-slate-400">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-400 opacity-70" />
              <p>Queue clean! No wrong ID approvals pending.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-6">Target User Details</th>
                    <th className="p-6">Location</th>
                    <th className="p-6">Submitted Wrong ID</th>
                    <th className="p-6">Coins Requested</th>
                    <th className="p-6">Amount (USD)</th>
                    <th className="p-6">Requested Date</th>
                    <th className="p-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {queue.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-6 font-bold text-slate-800">
                        {item.first_name} {item.last_name}
                        <p className="text-xs font-normal text-slate-400 mt-0.5">{item.email}</p>
                      </td>
                      <td className="p-6">
                        <CountryBadge country={item.country} />
                      </td>
                      <td className="p-6 font-mono text-slate-600 bg-red-50/50 rounded-lg p-2 max-w-xs truncate">{item.wrong_id}</td>
                      <td className="p-6 font-black text-pink-600">{parseInt(item.coins).toLocaleString()}</td>
                      <td className="p-6 font-bold text-slate-700">${parseFloat(item.amount).toFixed(2)}</td>
                      <td className="p-6 text-slate-500">{new Date(item.created_at).toLocaleString()}</td>
                      <td className="p-6 text-right">
                        <button
                          onClick={() => handleApproveWrongId(item.id)}
                          className="px-4 py-2 bg-green-500 text-white font-bold rounded-xl text-xs hover:bg-green-600 active:scale-95 transition shadow-md shadow-green-100"
                        >
                          Approve Correction
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default RechargeManagement;
